import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { Administrator, BrandingSetting, FeedBackCredential, OAuthCredential, OAuthData, TempTokenData } from 'mbs-ui-shared@models';
import TwoFactorComputers from 'mbs-ui-shared@models/TwoFactorComputers';
import { PassCheckType } from 'mbs-ui-shared@models/auth-models';
import { OneTimeAuthToken } from 'mbs-ui-shared@models/one-time-token.model';
import { ConfigurationService } from 'mbs-ui-shared@services';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OAuthCodeParams } from 'mbs-ui-shared@models/OAuthCredential';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly mbsAuthUrlPrefix = 'api/auth/';
  private readonly mbsProviderUrlPrefix = 'api/provider/';
  private readonly mbsStageUrlPrefix = 'api/login';
  private readonly tokenDataLocalStorageKey = 'tokenData';

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend, // for request without interceptors
    private config: ConfigurationService
  ) {}

  // localStorage part

  public getTokenData(): OAuthCredential {
    return JSON.parse(localStorage.getItem(this.tokenDataLocalStorageKey));
  }

  public setTokenData(tokenData: OAuthCredential): void {
    localStorage.setItem(this.tokenDataLocalStorageKey, JSON.stringify(tokenData));
  }

  public removeTokenData(): void {
    localStorage.removeItem(this.tokenDataLocalStorageKey);
  }

  // requests part

  public authTokenBySessionRequest$(): Observable<OAuthCredential> {
    return new HttpClient(this.httpBackend).get<OAuthCredential>(`${location.origin}/${this.mbsAuthUrlPrefix}token`);
  }

  public oauthTokenBySessionRequest$(options: OAuthCodeParams): Observable<OAuthCredential> {
    return this.http.post<OAuthCredential>(`${this.mbsAuthUrlPrefix}oauth/token`, { ...options });
  }

  public fetchUserRequest$(): Observable<Administrator & { BrandingSetting: BrandingSetting }> {
    return this.http.get<Administrator & { BrandingSetting: BrandingSetting }>(`${this.mbsAuthUrlPrefix}me`);
  }

  public localAgentLogout$(refreshToken: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.mbsAuthUrlPrefix}logout`, { refreshToken });
  }

  public appsTokenRequest$(request: { domain: string }): Observable<{ access_token: string }> {
    return this.http.post<{
      access_token: string;
    }>(`${this.mbsAuthUrlPrefix}new-token-scope/appsbackup-access`, request);
  }

  public permissionRequest$(payload: TwoFactorComputers): Observable<OAuthCredential> {
    return this.http.post<OAuthCredential>(`${this.mbsAuthUrlPrefix}new-token-scope/mbs-rm-access`, payload);
  }

  public getDropUserToken(): Observable<OAuthCredential> {
    return this.http.get<OAuthCredential>(`${this.mbsAuthUrlPrefix}token/mbs-drop-user`);
  }

  public loginRequest$({ email, password }): Observable<boolean> {
    return this.http.post(`${this.mbsStageUrlPrefix}/auth`, { email, password }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  public connectTokenRequest$(options: OAuthData): Observable<OAuthCredential> {
    const url = 'connect/token';
    const formData = new FormData();
    for (const key of Object.keys(options)) {
      formData.append(key, options[key]);
    }

    return this.http.post<OAuthCredential>(url, formData);
  }

  public oneTimeAuthTokenRequest$(): Observable<OneTimeAuthToken> {
    return this.http.get<OneTimeAuthToken>(`${this.mbsAuthUrlPrefix}onetimetoken`);
  }

  public feedBackTokenRequest$() {
    return this.http.get<FeedBackCredential>(`${this.mbsAuthUrlPrefix}new-token-scope/feedback-access`, {
      headers: { 'x-front-queue': 'true' }
    });
  }

  public checkPasswordStrengthRequest$(password: string): Observable<PassCheckType> {
    return this.http.post<PassCheckType>(`${this.mbsAuthUrlPrefix}passcheck`, { password });
  }

  public passwordRulesRequest(): Observable<string[]> {
    return this.http.get<string[]>(`${this.mbsAuthUrlPrefix}getpassrules`);
  }

  public generatePasswordRequest$(): Observable<string> {
    return this.http.get<string>(`${this.mbsAuthUrlPrefix}passgenerate`);
  }

  public changeEmailRequest$(value: { email: string; password: string }) {
    return this.http.put(`${this.mbsProviderUrlPrefix}email`, value);
  }

  public allowOfflineEditRequest$(state = true): Observable<null> {
    return this.http.put<null>(`${this.mbsAuthUrlPrefix}offlineedit`, { allowOfflineEdit: state });
  }

  // TODO: move to user service ?

  public tempTokenRequest$(userId: string, data: { tokenTarget: string; computerHid?: string }): Observable<TempTokenData> {
    return this.http.post<TempTokenData>(`api/users/${userId}/temptoken`, data);
  }

  // url part

  public getSupportPortalUrl(token: OneTimeAuthToken, path?: string): string {
    const returnUrl = `${location.origin}${RoutingPath.ApComputers}`;

    return `${this.config.get('supportUrl')}${path || ''}?OneTimeAuthToken=${encodeURIComponent(
      token.access_token
    )}&MbsReturnUrl=${returnUrl}`;
  }

  // legacy

  public prepareSubscriptionUrl(ownerId: string): string {
    return `/Admin/payment.aspx?type=Subscription&OwnerID=${ownerId}&customPrice=1&InvoiceID=`;
  }
}
